<template>
    <div class="container mortgage-calculator" :class="calculatorName">

		<div class="row justify-content-center calculator-header">
			<CalculatorHeader>
				<h2>Right to Buy Mortgage Calculator</h2>
				<h5>Fill out the few details below to see how we could help you achieve a mortgage</h5>
			</CalculatorHeader>
		</div>
		<div class="row justify-content-center calculator-body">
			<div class="col-md-10 col-xl-8">
				<form class="calculator-container">

					<div class="mt-5" @keyup="resetCalculation" @change="resetCalculation">
					
					<h4 class="calc-fieldset-title">Applicant Details</h4>

						<BooleanChoiceButtons
							fieldText="Number of applicants"
							buttonTextLeft="1"
							buttonTextRight="2"								
						>
							<template v-slot:left>
								<input type="radio" v-model="applicantsCount" :value="1">										
							</template>
							<template v-slot:right>
								<input type="radio" v-model="applicantsCount" :value="2">										
							</template>																	
						</BooleanChoiceButtons>

						<transition name="fade-x" mode="out-in">
							<InputElement
								v-show="applicantsCount !==0"	
								fieldText="Applicant 1 annual income (including benefits)?"
							>
								<InputTypeNumber currencyPlaceholder v-model="annualIncome1" />
							</InputElement>
						</transition>

						<transition name="fade-x" mode="out-in">
							<InputElement
								v-show="applicantsCount === 2"						
								fieldText="Applicant 2 annual income (including benefits)?"
							>
								<InputTypeNumber currencyPlaceholder v-model="annualIncome2" />
							</InputElement>
						</transition>
						
						<transition name="scale-in" mode="out-in">
							<ReturnedValueBox
								class="mb-5"
								text="Your household income is: "
								:value="householdIncome"
								v-show="annualIncome1 > 0 || annualIncome2 > 0"
								:innerElement=true
							/>
						</transition>

						<transition name="fade-x" mode="out-in">
							<InputElement					
								v-show="annualIncome1 > 0 || annualIncome2 > 0"
								fieldText="Number of dependant children?"
							>
								<InputTypeNumber v-model="dependantChildren" />
							</InputElement>
						</transition>

						<transition name="fade-x" mode="out-in">
							<div v-if="showPropertyDetails">
								<h4 class="calc-fieldset-title">Property Details</h4>

								<InputElement					
									fieldText="Value of property:"
								>
									<input class="form-control calc-form-control" type="number" v-model="valueProperty" min="0">
								</InputElement>								

								<transition name="fade-x" mode="out-in">
									<InputElement
										v-if="valueProperty"					
										fieldText="What is the discounted price?"
									>
										<input class="form-control calc-form-control" type="number" v-model="discountedPrice" min="0">
									</InputElement>
								</transition>				

								<transition name="fade-x" mode="out-in">
									<InputElement
										v-if="valueProperty && discountedPrice"				
										fieldText="Would you like to put in more deposit from your own resources, if so, how much:"
									>
										<input class="form-control calc-form-control" type="number" v-model="extraDeposit" min="0">
									</InputElement>
								</transition>					
							</div>
						</transition>

						<transition name="fade-x" mode="out-in">
                            <div class="row mt-5" v-if="showCalculateButton">
                                <div class="col-md-6 col-lg-4">Why are you using the calculator today</div>
                                <div class="col-md-6 col-lg-8 fcs-grid-long">
                                    <DropdownMenu :dropdownOptions="dropdownOptionsReason" v-model="calcReason1">
                                </div>
                            </div>
                        </transition>   

                        <transition name="fade-x" mode="out-in">
                            <div class="row mt-5" v-if="showCalculateButton">
                                <div class="col-md-6 col-lg-4">When will you be ready to move forward?</div>
                                <div class="col-md-6 col-lg-8 fcs-grid-long">
                                    <DropdownMenu :dropdownOptions="dropdownOptionsMove" v-model="calcReason2">
                                </div>
                            </div>
                        </transition> 

						<transition name="scale-in" mode="out-in">
							<div
								v-if="showCalculateButton"
								class="text-center mt-5 mb-3"
							>
								<div class="btn calc-btn-secondary" @click="calculate">CALCULATE</div>
							</div>
						</transition>

					</div>							

					<transition name="fade-y" mode="out-in">
						<div v-if="calcCompleted" ref="resultsContainer">

							<ResultsContainer class="mt-5 mb-5 calc-results-box">
								<div class="mb-3 results-title">{{this.chance}}</div>
							</ResultsContainer>

							<SubmissionForm :calculatorName="calculatorName" :calculatorData="collateMessageBody" />

						</div>
					</transition>

				</form>
			</div>				
		</div>

    </div>
</template>

<script>
import BooleanChoiceButtons from '@/components/utilities/booleanChoiceButtons.vue'
import CalculatorHeader from '@/components/utilities/calculatorHeader.vue'
import InputElement from '@/components/utilities/inputElement.vue'
import InputTypeNumber from '@/components/utilities/inputTypeNumber.vue'
import ResultsContainer from '@/components/utilities/resultsContainer.vue'
import ReturnedValueBox from '@/components/utilities/returnedValueBox.vue'
import SubmissionForm from '@/components/utilities/submissionForm.vue'
import DropdownMenu from '@/components/utilities/dropdownMenu.vue'
import axios from "axios"

export default {
	components: {
		BooleanChoiceButtons,
		CalculatorHeader,
		InputElement,
		InputTypeNumber,
		ResultsContainer,
		ReturnedValueBox,
		DropdownMenu,
		SubmissionForm				
	},	
    data() {
        return {
			calculatorName: 'right-to-buy-mortgage-calculator',
            applicantsCount: 0,
            annualIncome1: null,
            annualIncome2: null,
            dependantChildren: null,
			valueProperty: null,
            discountedPrice: null,
            extraDeposit: null,
			calculatorReasonTitle: null,
			calculatorReasonResults: null,
			calcReason1: "",
            calcReason2: "",
          dropdownOptionsReason: [
            {value: "Please select an option", text: "Please select an option"},
            {value: "I'm ready to move forward with a mortgage", text: "I'm ready to move forward with a mortgage"},
            {
              value: "I'm not ready for a mortgage but am curious about what I could borrow",
              text: "I'm not ready for a mortgage but am curious about what I could borrow"
            },
            {
              value: "I'm starting to save for a house and want to know what to aim for",
              text: "I'm starting to save for a house and want to know what to aim for"
            },
            {
              value: "I need to understand a what I can afford before house shopping",
              text: "I need to understand a what I can afford before house shopping"
            },
          ],

          dropdownOptionsMove: [
            {value: "Please select an option", text: "Please select an option"},
            {value: "12+ months", text: "12+ months"},
            {value: "6-12 months", text: "6-12 months"},
            {value: "In the next 3 months", text: "In the next 3 months"},
            {value: "As soon as possible", text: "As soon as possible"},
          ],
			chance: '',
			calcCompleted: false,
			submissionReason: {
				submitted: false,
				is_valid: false,
				confirmation_message: ''
			},
            formErrors: [],
            gfid: 3 //Please check the Gravity Form for corresponding form id (staging and live may vary!)
        }
    },
    methods: {
        calculate() {
            if( this.totalRequired <= this.householdIncome * 4 ) {
                this.chance =  `You've got a good chance, please submit your details below to obtain your most suitable Right to Buy mortgage advice and the most suitable rates.`;
            }
            if( (this.totalRequired > this.householdIncome * 4) && (this.totalRequired <= this.householdIncome * 5) ) {
                this.chance = `We may be able to get you a Right to Buy Mortgage, please submit your details below to obtain your most suitable Right to Buy mortgage advice and the most suitable rates.`;
            }
            if( (this.totalRequired > this.householdIncome * 5) && (this.totalRequired <= this.householdIncome * 7) ) {
                this.chance = `It's looking difficult, but please submit your details below to obtain your most suitable Right to Buy mortgage advice.`;
            }
            if( this.totalRequired > this.householdIncome * 7 ) {
                this.chance = `It looks unlikely, but please submit your details below to obtain your most suitable Right to Buy mortgage advice.`;
            }
			this.calcCompleted = true;
			this.calculatorReasonTitle = this.removeHyphensToUpperCase(this.calculatorName);
			this.calculatorReasonResults = 
			`
			Why are you using the calculator today: ${this.calcReason1}
			When will you be ready to move forward: ${this.calcReason2}
			`;
			this.$nextTick(() => {
                const resultsContainer = this.$refs.resultsContainer;

                if (resultsContainer) {
                    // Get the bounding rectangle of the element
                    const rect = resultsContainer.getBoundingClientRect();

                    // Calculate the target scroll position
                    const targetScrollPosition = window.scrollY + rect.top - 200;

                    // Scroll to the target position
                    window.scrollTo({
                        top: targetScrollPosition,
                        behavior: 'smooth'
                    });
                } else {
                    console.error('resultsContainer is undefined');
                }
            });
			var newReasonSubmission = new FormData();

			newReasonSubmission.append("form_id", this.gfid); //Please check the Gravity Form form id (staging and live may vary!)
			newReasonSubmission.append("input_1", this.calculatorReasonTitle);
			newReasonSubmission.append("input_3", this.calculatorReasonResults);

			this.postReasonSubmission(newReasonSubmission);
        },
		async postReasonSubmission(submissionReasonData) {
            try {

                const response = await axios.post(
                    `/wp-json/gf/v2/forms/`+ this.gfid + `/submissions`, //Please check the Gravity Form form id (staging and live may vary!)
                    submissionReasonData,
                    {
                        headers: {"Content-Type": "application/x-www-form-urlencoded"}
                    }
                );
                
                const submissionReason = response;
                // console.log(submission)

                if ( submissionReason.data.is_valid ) {
                    this.submissionReason.submitted = true;
                    this.submissionReason.is_valid = true;
                }
            } catch(error) {
                // console.log(error)
            }
        },
        removeHyphensToUpperCase(str) {
            return str.replace(/(-|^)([^-]?)/g, function(_, prep, letter) { return (prep && ' ') + letter.toUpperCase(); });
		},
		resetCalculation() {
			this.calcCompleted = false;
		}				
    },	
    computed: {
		showPropertyDetails() {
			return this.householdIncome && (this.dependantChildren || this.dependantChildren === 0);
		},		
		showCalculateButton() {
			return this.discountedPrice && (this.extraDeposit || this.extraDeposit === 0)
		},
        householdIncome() {
            let annualIncome1 = this.annualIncome1 ? this.annualIncome1 : 0;
            let annualIncome2 = this.annualIncome2 ? this.annualIncome2 : 0;

            return annualIncome1 + annualIncome2;
        },
        totalRequired() {
            let discountedPrice = this.discountedPrice ? this.discountedPrice : 0;
            let extraDeposit = this.extraDeposit ? this.extraDeposit : 0;

            return discountedPrice - extraDeposit;
        },
		collateMessageBody() {
			const calculatorData = {
				calculatorTitle: this.removeHyphensToUpperCase(this.calculatorName),
				calculatorResults:
				`
					Number of applicants: ${this.applicantsCount}
					Applicant 1 income: ${parseInt(this.annualIncome1)}
					Applicant 2 income: ${parseInt(this.annualIncome2)}
					Household income: £${this.householdIncome}
                    Number of dependant children: ${parseInt(this.dependantChildren)}
					Property Value: £ ${parseInt(this.valueProperty)}
					What is the Right to Buy discounted price? £${parseInt(this.discountedPrice)}
					Deposit amount: £${parseInt(this.extraDeposit)}
                    Total required: £${this.totalRequire}
					Why are you using the calculator today: ${this.calcReason1}
                    When will you be ready to move forward: ${this.calcReason2}
				`
			}
			
			return calculatorData;
		}			
    }
}
</script>

<style lang="scss">
	@import './styles/calcs.scss';
</style>